import { ReactNode, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ActiveAdmin,
  ActiveAudit,
  ActiveCoupon,
  ActiveCredit,
  ActiveCustomer,
  ActiveHome,
  ActiveOrder,
  ActiveProduct,
  ActiveReport,
  ActiveSettings,
  ActiveStore,
  InactiveAdmin,
  InactiveAudit,
  InactiveCoupon,
  InactiveCreditt,
  InActiveCustomer,
  InActiveHome,
  InactiveOrder,
  InActiveProduct,
  InActiveReport,
  InactiveSettings,
  InactiveStore,
} from "./icons";
import withAuth from "../hoc/useAuth";
import { useGetMeQuery } from "../hooks/Auth/useGetMeQuery";
import { Avatar, Dropdown, Menu, message } from "antd";
import WelcomeLoader from "../Components/WelcomeLoader";
import useAutoLogout from "../hooks/useAutoLogout";
import { cn } from "utils";
import ProtectedComponent from "./SidebarOptions/ProtectedComponents";
import { FaSortDown } from "react-icons/fa6";

const DashboardLayout = ({
  children,
  title,
  right,
  back,
  className,
}: {
  children: ReactNode;
  title: string;
  right: ReactNode;
  back: boolean;
  className?: string;
}) => {
  const Logo = require("../assets/logo.png");
  let location = useLocation();
  const navigate = useNavigate();
  const { data: user } = useGetMeQuery();
  useAutoLogout();

  const goBack = () => {
    navigate(-1);
  };

  const allowedModules = user?.data?.role?.permission
    .filter((permission) => permission.module.active)
    .map((permission) => permission.module.key);

  useEffect(() => {
    const roleKey = user?.data?.role?.key;

    const checkAuthorization = () => {
      if (roleKey && roleKey !== "driver") {
        return;
      }

      if (!roleKey || roleKey === "driver") {
        localStorage.clear();
        message.error(
          "Unauthorized access. You do not have permission to view this route."
        );
        navigate("/");
      }
    };

    const timeoutId = setTimeout(checkAuthorization, 3000);

    return () => clearTimeout(timeoutId);
  }, [navigate, user?.data?.role]);

  const menu = (
    <Menu>
      <Menu.Item key="2">
        <Link to="/change-password" className="text-center">
          Change Password
        </Link>
      </Menu.Item>
      <Menu.Item key="3" style={{ background: "#FF5011" }}>
        <div
          style={{ borderRadius: "4px", color: "white", textAlign: "center" }}
          onClick={() => {
            navigate("/");
            localStorage.clear();
            message.success("Logout successful");
          }}
        >
          Log Out
        </div>
      </Menu.Item>
    </Menu>
  );

  return (
    <div className="flex mx-auto max-w-[2000px] h-dvh overflow-hidden admin-dashboard">
      {/* <CheckUserRole userRole={user?.data?.role?.key ?? ""} /> */}
      <WelcomeLoader />
      <div className="min-w-[200px] border-r-[1px] relative border-[#eeeeee] px-[40px] h-dvh overflow-y-scroll">
        <img src={Logo} alt="Logo" className="h-[25px] mt-[30px]" />
        <div className="mt-[20px]">
          <p className="text-[#818181] font-medium mb-[10px] text-[12px]">
            For you
          </p>
          <div className="text-[14px]">
            <Link
              to="/dashboard"
              className={`flex items-center gap-[8px] mb-1 py-[8px] text-[13px] ${
                location?.pathname.includes("/dashboard")
                  ? "text-orange"
                  : "text-[#3e3e3e]"
              }`}
            >
              {location?.pathname.includes("/dashboard") ? (
                <ActiveHome />
              ) : (
                <InActiveHome />
              )}
              Dashboard
            </Link>
            <ProtectedComponent
              roleKey={user?.data?.role?.key}
              allowedModules={allowedModules}
              requiredModule="product-management"
            >
              <>
                <Link
                  to="/product-management"
                  className={`flex items-center gap-[8px] mb-1 py-[8px] text-[13px] ${
                    location?.pathname.includes("/product-management")
                      ? "text-orange"
                      : "text-[#3e3e3e]"
                  }`}
                >
                  {location?.pathname.includes("/product-management") ? (
                    <ActiveProduct />
                  ) : (
                    <InActiveProduct />
                  )}
                  <span className="flex-1">
                    <div className="flex justify-between items-center">
                      Products
                      <span>
                        {location?.pathname.includes("/product-management") ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="size-4 text-orange"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="m19.5 8.25-7.5 7.5-7.5-7.5"
                            />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="size-4"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="m4.5 15.75 7.5-7.5 7.5 7.5"
                            />
                          </svg>
                        )}
                      </span>
                    </div>
                  </span>
                </Link>
                {location?.pathname.includes("/product-management") && (
                  <div className="ml-[28px]">
                    <Link
                      to="/product-management"
                      className={`flex items-center gap-[8px] mb-1 py-[8px] text-[13px] ${
                        location?.pathname === "/product-management"
                          ? "text-orange"
                          : "text-[#3e3e3e]"
                      }`}
                    >
                      Catalog
                    </Link>
                    <Link
                      to="/product-management/category"
                      className={`flex items-center gap-[8px] mb-1 py-[8px] text-[13px] ${
                        location?.pathname.includes(
                          "/product-management/category"
                        )
                          ? "text-orange"
                          : "text-[#3e3e3e]"
                      }`}
                    >
                      Category
                    </Link>
                  </div>
                )}
              </>
            </ProtectedComponent>

            <ProtectedComponent
              roleKey={user?.data?.role?.key}
              allowedModules={allowedModules}
              requiredModule="customer-management"
            >
              <Link
                to="/customer-management"
                className={`flex items-center gap-[8px] mb-1 py-[8px] text-[13px] ${
                  location?.pathname.includes("/customer-management")
                    ? "text-orange"
                    : "text-[#3e3e3e]"
                }`}
              >
                {location?.pathname.includes("/customer-management") ? (
                  <ActiveCustomer />
                ) : (
                  <InActiveCustomer />
                )}
                Customer management
              </Link>
            </ProtectedComponent>
            <ProtectedComponent
              roleKey={user?.data?.role?.key}
              allowedModules={allowedModules}
              requiredModule="order-management"
            >
              <Link
                to="/order-management"
                className={`flex items-center gap-[8px] mb-1 py-[8px] text-[13px] ${
                  location?.pathname.includes("/order-management")
                    ? "text-orange"
                    : "text-[#3e3e3e]"
                }`}
              >
                {location?.pathname.includes("/order-management") ? (
                  <ActiveOrder />
                ) : (
                  <InactiveOrder />
                )}
                Order Management
              </Link>
            </ProtectedComponent>

            <ProtectedComponent
              roleKey={user?.data?.role?.key}
              allowedModules={allowedModules}
              requiredModule="credit-management"
            >
              <Link
                to="/credit-management"
                className={`flex items-center gap-[8px] mb-1 py-[8px] text-[13px] ${
                  location?.pathname.includes("/credit-management")
                    ? "text-orange"
                    : "text-[#3e3e3e]"
                }`}
              >
                {location?.pathname.includes("/credit-management") ? (
                  <ActiveCredit />
                ) : (
                  <InactiveCreditt />
                )}
                Credit Management
              </Link>
            </ProtectedComponent>

            <ProtectedComponent
              roleKey={user?.data?.role?.key}
              allowedModules={allowedModules}
              requiredModule="report-management"
            >
              <Link
                to="/report"
                className={`flex items-center gap-[8px] mb-1 py-[8px] text-[13px] ${
                  location?.pathname.includes("/report")
                    ? "text-orange"
                    : "text-[#3e3e3e]"
                }`}
              >
                {location?.pathname.includes("/report") ? (
                  <ActiveReport />
                ) : (
                  <InActiveReport />
                )}
                Report
              </Link>
            </ProtectedComponent>
            <ProtectedComponent
              roleKey={user?.data?.role?.key}
              allowedModules={allowedModules}
              requiredModule="audit-trail"
            >
              <Link
                to="/audit-trail"
                className={`flex items-center gap-[8px] mb-1 py-[8px] text-[13px] ${
                  location?.pathname.includes("/audit-trail")
                    ? "text-orange"
                    : "text-[#3e3e3e]"
                }`}
              >
                {location?.pathname.includes("/audit-trail") ? (
                  <ActiveAudit />
                ) : (
                  <InactiveAudit />
                )}
                Audit Trail
              </Link>
            </ProtectedComponent>
            <ProtectedComponent
              roleKey={user?.data?.role?.key}
              allowedModules={allowedModules}
              requiredModule="discount-management"
            >
              <Link
                to="/discount-management"
                className={`flex items-center gap-[8px] mb-1 py-[8px] text-[13px] ${
                  location?.pathname.includes("/discount-management")
                    ? "text-orange"
                    : "text-[#3e3e3e]"
                }`}
              >
                {location?.pathname.includes("/discount-management") ? (
                  <ActiveCoupon />
                ) : (
                  <InactiveCoupon />
                )}
                Discount Management
              </Link>
            </ProtectedComponent>
            <ProtectedComponent
              roleKey={user?.data?.role?.key}
              allowedModules={allowedModules}
              requiredModule="branch-management"
            >
              <Link
                to="/branch-management"
                className={`flex items-center gap-[8px] mb-1 py-[8px] text-[13px] ${
                  location?.pathname.includes("/branch-management")
                    ? "text-orange"
                    : "text-[#3e3e3e]"
                }`}
              >
                {location?.pathname.includes("/branch-management") ? (
                  <ActiveStore />
                ) : (
                  <InactiveStore />
                )}
                Branch
              </Link>
            </ProtectedComponent>
            <ProtectedComponent
              roleKey={user?.data?.role?.key}
              allowedModules={allowedModules}
              requiredModule="admin-management"
            >
              <Link
                to="/admin-management"
                className={`flex items-center gap-[8px] mb-1 py-[8px] text-[13px] ${
                  location?.pathname.includes("/admin-management")
                    ? "text-orange"
                    : "text-[#3e3e3e]"
                }`}
              >
                {location?.pathname.includes("/admin-management") ? (
                  <ActiveAdmin />
                ) : (
                  <InactiveAdmin />
                )}
                Admin Management
              </Link>
            </ProtectedComponent>
            <Link
              to="/profile"
              className={`flex items-center gap-[8px] mb-1 py-[8px] text-[13px] ${
                location?.pathname.includes("/profile")
                  ? "text-orange"
                  : "text-[#3e3e3e]"
              }`}
            >
              {location?.pathname.includes("/profile") ? (
                <ActiveSettings />
              ) : (
                <InactiveSettings />
              )}
              Profile
            </Link>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 px-[40px] bg-white pb-[20px] w-full text-[14px]">
          <div style={{ cursor: "pointer" }}>
            <Dropdown overlay={menu} trigger={["hover"]}>
              <div className="flex gap-2">
                <Avatar
                  style={{
                    backgroundColor: "#562B00",
                    verticalAlign: "middle",
                  }}
                  size="large"
                >
                  {user && user?.data?.firstName.charAt(0)}
                </Avatar>
                <div className="flex-1 flex justify-between items-center">
                  <span className="overflow-hidden">
                    <h3 className="truncate text-black font-bold">
                      {user &&
                        user?.data?.firstName + " " + user?.data?.lastName}
                    </h3>
                    <p className="truncate text-grey mt-[1px] text-[12px]">
                      {user && user?.data?.role?.name}
                    </p>
                  </span>
                  <FaSortDown size={20} />
                </div>
              </div>
            </Dropdown>
          </div>
        </div>
      </div>
      <div className="flex-1">
        <div className="p-[18px] pr-[40px] border-b-[1px] border-[#eee] text-[16px] text-black font-medium flex justify-between items-center">
          <h3 className="text-[18px] flex gap-2">
            {back && (
              <span className="inline cursor-pointer" onClick={goBack}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18"
                  />
                </svg>
              </span>
            )}
            {title}
          </h3>
          <div className="flex gap-3 items-center">{right}</div>
        </div>
        <div className={cn("overflow-y-scroll h-[85%]", className)}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default withAuth(DashboardLayout);
